import {
  getCoverImageUrl,
  getMediaUrl,
  isVideoMedia,
} from "@src/features/contentDetail/utils/media";
import { getLinkType, LinkType } from "@src/lib/utils/files";
import { ReactElement } from "react";

import { MediaGalleryItem } from "@src/lib/ui/MediaGallery";
import PostMediaTile, { PostMediaTileMedia } from "./PostMediaTile";

export const sortMediaAttachments = <T extends { index: number }>(
  medias: readonly T[],
): T[] => [...medias].sort((a, b) => a.index - b.index);

export type GalleryEntry = {
  item: MediaGalleryItem;
  title?: string;
  libraryItemId?: string;
  pdfUrl?: string;
};

export const toGalleryEntries = (
  medias: readonly PostMediaTileMedia[],
): GalleryEntry[] =>
  sortMediaAttachments(medias).flatMap((attachment): GalleryEntry[] => {
    if (attachment.media) {
      const url = getMediaUrl(attachment.media);
      if (getLinkType(url) === LinkType.PDF) {
        const item: MediaGalleryItem = attachment.media.thumbnailImageUrl
          ? { url: attachment.media.thumbnailImageUrl, type: "image" }
          : { url, type: "pdf" };
        return [{ item, pdfUrl: url }];
      }
      return [
        {
          item: {
            url,
            type: isVideoMedia(attachment.media) ? "video" : "image",
          },
        },
      ];
    }
    if (attachment.libraryItem?.coverImage) {
      const url = getCoverImageUrl(attachment.libraryItem.coverImage);
      if (url)
        return [
          {
            item: { url, type: "image" },
            title: attachment.libraryItem.name.en,
            libraryItemId: attachment.libraryItem.id,
          },
        ];
    }
    return [];
  });

export const toGalleryItems = (
  medias: readonly PostMediaTileMedia[],
): MediaGalleryItem[] => toGalleryEntries(medias).map((entry) => entry.item);

export const toMediaTiles = (
  medias: readonly PostMediaTileMedia[],
  onTileClick?: (index: number) => void,
): ReactElement[] =>
  sortMediaAttachments(medias).map((attachment, index) => (
    <PostMediaTile
      key={attachment.id}
      attachment={attachment}
      onClick={onTileClick ? () => onTileClick(index) : undefined}
    />
  ));
